import React from 'react';
import { Rate, Tooltip } from 'app/seedly-component-library';
import TruncateMarkup from 'react-truncate-markup';
import clsx from 'clsx';

import { imageUrl as themeImageUrl } from 'theme';
import useIsMobile from 'app/utils/hooks/useIsMobile';

interface OwnProps {
  imageUrl: string;
  title: string;
  rating: number;
  reviewsCount: number;
  productUrl: string;
  variant: 'widget' | 'sm' | 'md' | 'lg' | 'xl';
  claimed: boolean;
  onTitleClick: () => void;
}

const ImageSize = {
  xl: 80,
  lg: 72,
  md: 64,
  sm: 56,
};

const ProductPassportBase = (props: OwnProps) => {
  const {
    imageUrl = '',
    title = '',
    rating,
    reviewsCount = 0,
    productUrl = '',
    variant = 'sm',
    claimed = false,
    onTitleClick = () => {},
  } = props;

  const score = (rating && rating.toFixed(1)) || 'No rating yet';
  const reviewCount = `${reviewsCount || 0} Reviews`;

  const isMobile = useIsMobile();

  return (
    <div className={clsx('flex gap-2 items-center')}>
      <img
        src={imageUrl}
        alt={title}
        className={clsx(
          'max-w-full h-auto rounded border border-neutral-400 border-solid',
          (variant === 'sm' || variant === 'widget') &&
            'min-w-[56px] min-h-[56px]',
          variant === 'md' && 'min-w-[64px] min-h-[64px]',
          variant === 'lg' && 'min-w-[72px] min-h-[72px]',
          variant === 'xl' && 'min-w-[80px] min-h-[80px]',
        )}
        height={ImageSize[variant]}
        width={ImageSize[variant]}
      />
      <div className="flex flex-col gap-1">
        <div className="flex gap-1 items-center">
          <TruncateMarkup lines={2}>
            {productUrl ? (
              <a
                className={clsx(
                  variant === 'sm' || (variant === 'md' && 'text-sm'),
                  variant === 'widget' && 'text-sm',
                  variant === 'lg' && 'text-base',
                  variant === 'xl' && 'text-xl',
                  'font-bold underline',
                )}
                target="_blank"
                href={productUrl}
                rel="noopener noreferrer"
                data-testid="product-item"
                onClick={onTitleClick}
              >
                <h2
                  className={
                    variant === 'widget' &&
                    'w-[210px] text-ellipsis whitespace-nowrap overflow-hidden'
                  }
                >
                  {title}
                </h2>
              </a>
            ) : (
              <p
                className={clsx(
                  variant === 'sm' || (variant === 'md' && 'text-sm'),
                  variant === 'widget' && 'text-sm',
                  variant === 'lg' && 'text-base',
                  variant === 'xl' && 'text-xl',
                  'font-bold',
                )}
              >
                <h2
                  className={
                    variant === 'widget' &&
                    'w-[210px] text-ellipsis whitespace-nowrap overflow-hidden'
                  }
                >
                  {title}
                </h2>
              </p>
            )}
          </TruncateMarkup>
          {claimed && (
            <Tooltip
              showToolTip={!isMobile}
              placement="top"
              renderTriggerButton={() => {
                return (
                  <img
                    src={themeImageUrl.verifiedInverted}
                    alt="Verified Account"
                  />
                );
              }}
              renderToolTipBody={() => (
                <div className="flex flex-wrap text-center">
                  A Verified Blue Tick confirms that the official company
                  manages this product/service on Seedly.
                </div>
              )}
            />
          )}
        </div>

        <div className="flex gap-2 items-center">
          <p className="text-sm font-bold text-green-500">{score}</p>
          {Boolean(rating) && (
            <Rate
              size={14}
              disabled
              starMargin="4px 0 0 0"
              defaultValue={rating}
            />
          )}
          <div className="vertical-divider" />
          <p>{reviewCount}</p>
        </div>
      </div>
    </div>
  );
};

export default ProductPassportBase;
